import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Leaf, Mail, Lock, ArrowRight, AlertCircle } from "lucide-react";

const BG = "https://static.prod-images.emergentagent.com/jobs/9f587cd0-f1dc-4f2b-8580-50f35b2a5e36/images/c71812ddbc7574d253e489662f4dc5559bfa64fdc6122f4034fcca5df8b7aba5.png";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/app/dashboard");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const fillDemo = (em) => {
    setEmail(em);
    setPassword(em === "admin@greencon.id" ? "Admin123!" : "Demo123!");
  };

  return (
    <div className="min-h-screen flex">
      {/* Left brand panel */}
      <div className="hidden lg:flex w-1/2 relative bg-slate-900 overflow-hidden">
        <img src={BG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-br from-green-900/70 to-slate-950/80" />
        <div className="relative z-10 flex flex-col justify-between p-12 text-white">
          <Link to="/" className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-xl bg-green-600 flex items-center justify-center">
              <Leaf className="h-6 w-6 text-white" />
            </div>
            <div>
              <div className="font-heading font-bold text-lg">SIA-HPP GREENCON</div>
              <div className="text-xs text-green-300">Politeknik Negeri Manado</div>
            </div>
          </Link>
          <div>
            <h2 className="font-heading text-4xl font-bold leading-tight">
              Akuntansi HPP berbasis<br /> Green Accounting & AI
            </h2>
            <p className="text-slate-300 mt-4 max-w-md">
              Hitung Green HPP, biaya lingkungan, emisi karbon, dan dapatkan rekomendasi efisiensi untuk produk konstruksi ramah lingkungan.
            </p>
          </div>
          <p className="text-xs text-slate-400">© {new Date().getFullYear()} Politeknik Negeri Manado · SIA-HPP GREENCON</p>
        </div>
      </div>

      {/* Right form */}
      <div className="flex-1 flex items-center justify-center bg-slate-50 p-6">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-2.5 mb-8 justify-center">
            <div className="h-10 w-10 rounded-lg bg-green-700 flex items-center justify-center">
              <Leaf className="h-5 w-5 text-white" />
            </div>
            <div className="font-heading font-bold text-slate-900">SIA-HPP GREENCON</div>
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8">
            <h1 className="font-heading text-2xl font-bold text-slate-900">Selamat Datang</h1>
            <p className="text-sm text-slate-500 mt-1">Masuk untuk mengakses sistem akuntansi Green HPP.</p>

            {error && (
              <div className="mt-5 flex items-start gap-2 rounded-lg bg-red-50 border border-red-200 px-3 py-2.5 text-sm text-red-700" data-testid="login-error">
                <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" /> {error}
              </div>
            )}

            <form onSubmit={submit} className="mt-6 space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="email" className="text-xs font-semibold text-slate-600">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                    placeholder="nama@greencon.id" className="pl-9" data-testid="login-email-input" />
                </div>
              </div>
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password" className="text-xs font-semibold text-slate-600">Password</Label>
                  <Link to="/forgot-password" className="text-xs text-green-700 hover:underline" data-testid="forgot-link">Lupa password?</Link>
                </div>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••" className="pl-9" data-testid="login-password-input" />
                </div>
              </div>
              <Button type="submit" disabled={loading} className="w-full bg-green-700 hover:bg-green-800 h-11" data-testid="login-submit-button">
                {loading ? "Memproses..." : <>Masuk <ArrowRight className="h-4 w-4 ml-1.5" /></>}
              </Button>
            </form>

            <div className="mt-6 pt-5 border-t border-slate-100">
              <p className="text-xs text-slate-400 mb-2">Akun demo (klik untuk isi otomatis):</p>
              <div className="flex flex-wrap gap-2">
                {[["admin@greencon.id", "Super Admin"], ["direktur@greencon.id", "Direktur"], ["akuntansi@greencon.id", "Akuntansi"], ["lingkungan@greencon.id", "Env. Officer"]].map(([em, lbl]) => (
                  <button key={em} type="button" onClick={() => fillDemo(em)} data-testid={`demo-${lbl.toLowerCase().replace(/[^a-z]/g,'')}`}
                    className="text-xs px-2.5 py-1 rounded-full bg-slate-100 hover:bg-green-100 text-slate-600 hover:text-green-800 transition-colors">
                    {lbl}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
